import React, { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Chip from '@mui/material/Chip';
import Autocomplete from '@mui/material/Autocomplete';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PreviewIcon from '@mui/icons-material/Preview';
import SaveIcon from '@mui/icons-material/Save';
import IconButton from '@mui/material/IconButton';
import Checkbox from '@mui/material/Checkbox';
import { Card } from '../components/ui-kit';
import {
  getNotificationTemplateById,
  updateNotificationTemplate,
  previewNotificationTemplate,
  getAvailableSegments,
} from '../api/notificationTemplates';
import { getRoles } from '../api/roles';

export default function NotificationTemplateEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const titleRef = useRef(null);
  const messageRef = useRef(null);

  const [form, setForm] = useState(null);
  const [preview, setPreview] = useState(null);
  const [applyToExisting, setApplyToExisting] = useState(false);
  const [segmentSuggestions, setSegmentSuggestions] = useState(['personal', 'company']);
  const [availableRoles, setAvailableRoles] = useState(['super-admin']);

  const { isLoading } = useQuery({
    queryKey: ['notification-template', id],
    queryFn: () => getNotificationTemplateById(id),
    onSuccess: (data) => {
      if (!form) setForm({ ...data });
    },
  });

  useQuery({
    queryKey: ['notification-template-segments'],
    queryFn: getAvailableSegments,
    onSuccess: (data) => setSegmentSuggestions(data),
  });

  useQuery({
    queryKey: ['roles-for-templates'],
    queryFn: () => getRoles({ limit: 100 }),
    onSuccess: (data) => {
      const names = (data.roles || data || []).map((r) => r.name).filter(Boolean);
      if (names.length) setAvailableRoles(names);
    },
  });

  const saveMutation = useMutation({
    mutationFn: () =>
      updateNotificationTemplate(id, {
        title: form.title,
        message: form.message,
        type: form.type,
        category: form.category,
        routing: form.routing,
        targetRoles: form.targetRoles,
        segments: form.segments,
        mandatory: form.mandatory,
        isActive: form.isActive,
        description: form.description,
        availableFields: form.availableFields,
        applyToExisting,
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries(['notification-templates']);
      queryClient.invalidateQueries(['notification-template', id]);
      const msg = data.updatedExisting
        ? `Saved. Updated ${data.updatedExisting} existing notifications.`
        : 'Saved successfully.';
      toast.success(msg);
    },
    onError: () => toast.error('Failed to save template'),
  });

  const previewMutation = useMutation({
    mutationFn: () => previewNotificationTemplate(id),
    onSuccess: (data) => setPreview(data),
    onError: () => toast.error('Preview failed'),
  });

  const lastFocusedRef = useRef('message');

  const handleFieldFocus = (fieldName) => {
    lastFocusedRef.current = fieldName;
  };

  const insertField = (field) => {
    const tag = `{{${field}}}`;
    const key = lastFocusedRef.current;
    const ref = key === 'title' ? titleRef : messageRef;
    const el = ref?.current?.querySelector('input, textarea');
    if (el) {
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const val = form[key] || '';
      setForm({ ...form, [key]: val.slice(0, start) + tag + val.slice(end) });
    } else {
      setForm({ ...form, [key]: (form[key] || '') + tag });
    }
  };

  if (isLoading || !form) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/notification-templates')}>
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Edit Template
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            {form.eventType}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        {/* Main form */}
        <Card sx={{ flex: '1 1 500px', p: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Template Content
          </Typography>

          <TextField
            ref={titleRef}
            label="Title"
            fullWidth
            size="small"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            onFocus={() => handleFieldFocus('title')}
            helperText="Use {{field}} for dynamic values"
            sx={{ mb: 2 }}
          />

          <TextField
            ref={messageRef}
            label="Message"
            fullWidth
            size="small"
            multiline
            rows={3}
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            onFocus={() => handleFieldFocus('message')}
            helperText='Use {{field || "fallback"}} for dynamic values with defaults'
            sx={{ mb: 2 }}
          />

          <TextField
            label="Description"
            fullWidth
            size="small"
            value={form.description || ''}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            helperText="Internal description for admins"
            sx={{ mb: 2 }}
          />

          {/* Available fields */}
          {form.availableFields?.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                Available fields (click to insert into {lastFocusedRef.current}):
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {form.availableFields.map((field) => (
                  <Chip
                    key={field}
                    label={`{{${field}}}`}
                    size="small"
                    variant="outlined"
                    onClick={() => insertField(field)}
                    sx={{ cursor: 'pointer', fontFamily: 'monospace', fontSize: '0.7rem' }}
                  />
                ))}
              </Box>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Delivery Settings
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Routing</InputLabel>
              <Select
                value={form.routing}
                label="Routing"
                onChange={(e) => setForm({ ...form, routing: e.target.value })}
              >
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="role">Role</MenuItem>
                <MenuItem value="user+role">User + Role</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={form.type}
                label="Type"
                onChange={(e) => setForm({ ...form, type: e.target.value })}
              >
                <MenuItem value="info">Info</MenuItem>
                <MenuItem value="success">Success</MenuItem>
                <MenuItem value="warning">Warning</MenuItem>
                <MenuItem value="error">Error</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Target Roles — visible only for role/user+role routing */}
          {(form.routing === 'role' || form.routing === 'user+role') && (
            <Autocomplete
              multiple
              freeSolo
              size="small"
              options={availableRoles}
              value={form.targetRoles || ['super-admin']}
              onChange={(_, newValue) => setForm({ ...form, targetRoles: newValue })}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip variant="outlined" label={option} size="small" {...getTagProps({ index })} key={option} />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Target Roles"
                  helperText="Which roles receive this notification"
                />
              )}
              sx={{ mb: 2 }}
            />
          )}

          {/* Segments */}
          <Autocomplete
            multiple
            freeSolo
            size="small"
            options={segmentSuggestions}
            value={form.segments || []}
            onChange={(_, newValue) => setForm({ ...form, segments: newValue })}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip variant="outlined" label={option} size="small" {...getTagProps({ index })} key={option} />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Segments"
                helperText="Which tabs this notification appears in. Type to add custom values."
              />
            )}
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.mandatory}
                  onChange={(e) => setForm({ ...form, mandatory: e.target.checked })}
                />
              }
              label="Mandatory (cannot be disabled by user)"
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Save actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={applyToExisting}
                  onChange={(e) => setApplyToExisting(e.target.checked)}
                  size="small"
                />
              }
              label="Apply changes to existing notifications"
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isLoading}
            >
              {saveMutation.isLoading ? 'Saving...' : 'Save'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<PreviewIcon />}
              onClick={() => previewMutation.mutate()}
              disabled={previewMutation.isLoading}
            >
              Preview
            </Button>
          </Box>
        </Card>

        {/* Preview panel */}
        <Card sx={{ flex: '0 0 360px', p: 3, alignSelf: 'flex-start' }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Preview
          </Typography>

          {preview ? (
            <Box>
              <Alert severity={preview.type || 'info'} sx={{ mb: 2 }}>
                <Typography variant="subtitle2">{preview.title}</Typography>
                <Typography variant="body2">{preview.message}</Typography>
              </Alert>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                <Chip label={preview.routing} size="small" color="primary" />
                <Chip label={preview.category} size="small" variant="outlined" />
                {(preview.segments || []).map((s) => (
                  <Chip key={s} label={s} size="small" variant="outlined" />
                ))}
              </Box>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Click "Preview" to see how the notification will look with sample data.
            </Typography>
          )}
        </Card>
      </Box>
    </Box>
  );
}
